import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Alert, Button, EmptyState, PageLayout, Spinner } from '@sentinel/ui';
import { useAuditLog, type AuditLogFilters } from '../features/audit-log/use-audit-log';
import { useAuditExport } from '../features/audit-log/use-audit-export';
import { AuditRow } from '../features/audit-log/components/audit-row';
import { AuditFilterBar } from '../features/audit-log/components/audit-filter-bar';
import { AuditLogIcon } from '../features/account/components/nav-icons';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const styles = stylex.create({
  exportRow: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  list: {
    display: 'grid',
    gap: '0.625rem',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  loadingWrap: {
    display: 'grid',
    placeItems: 'center',
    paddingTop: '3rem',
    paddingBottom: '3rem',
  },
  loadMoreWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '0.5rem',
  },
  emptyGlyph: {
    width: '2.5rem',
    height: '2.5rem',
    display: 'grid',
    placeItems: 'center',
    color: sentinel.textSubtle, // sentinel.textSubtle
  },
});

const INITIAL_FILTERS: AuditLogFilters = { eventTypes: [], dateFrom: null, dateTo: null };

function AuditLogPage() {
  const { t } = useTranslation('audit-log');
  const [filters, setFilters] = useState<AuditLogFilters>(INITIAL_FILTERS);
  const { entries, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useAuditLog(filters);
  const exporter = useAuditExport();

  return (
    <PageLayout
      title={t('title')}
      subtitle={t('subtitle')}
      actions={
        <div {...stylex.props(styles.exportRow)}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={exporter.isPending}
            onClick={() => { void exporter.mutate({ ...filters, format: 'csv' }); }}
          >
            {t('export.csv')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={exporter.isPending}
            onClick={() => { void exporter.mutate({ ...filters, format: 'jsonl' }); }}
          >
            {t('export.jsonl')}
          </Button>
        </div>
      }
    >
      <AuditFilterBar value={filters} onChange={setFilters} />

      {isError ? (
        <Alert type="error" role="alert">
          {t('loadError')}
        </Alert>
      ) : isLoading ? (
        <div {...stylex.props(styles.loadingWrap)}>
          <Spinner label={t('loading')} />
        </div>
      ) : entries.length === 0 ? (
        <div data-testid="audit-log-empty">
          <EmptyState
            title={t('empty')}
            icon={
              <span {...stylex.props(styles.emptyGlyph)}>
                <AuditLogIcon />
              </span>
            }
          />
        </div>
      ) : (
        <ul {...stylex.props(styles.list)} data-testid="audit-log-list">
          {entries.map((e) => (
            <AuditRow key={e.id} entry={e} />
          ))}
        </ul>
      )}

      {hasNextPage ? (
        <div {...stylex.props(styles.loadMoreWrap)}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => { void fetchNextPage(); }}
          >
            {t('loadMore')}
          </Button>
        </div>
      ) : null}
    </PageLayout>
  );
}

export const Route = createFileRoute('/account/audit-log')({
  component: AuditLogPage,
});
